import * as React from 'react';
import { ConnectDragPreview, ConnectDragSource, DragSource } from 'react-dnd';

import * as Service from 'types/service';
import { Button } from 'bento';
import { DndItemTypes } from 'constants/dndItemTypes';
import { IButton } from '@tock/shared/components/buttons/DeprecatedButton';
import { IconGrab } from '@tock/icons';
import { PartyDragSource, collectSource, partySource } from 'components/service/Party';
import { ServicePartyDragMode } from 'state/service/ui';
import { css } from '@emotion/react';
import { getEmptyImage } from 'react-dnd-html5-backend';

export interface IIconButton extends IButton {
  className?: string;
  iconSize?: 'medium' | 'large';
  modifierClassName?: string;
  stretch?: boolean;
  text?: string;
  onPointerDown?(event: React.PointerEvent<HTMLElement>): void;
  onClick?(event: React.MouseEvent<HTMLElement>): void;
  children?: React.ReactNode;
  party: Service.Party;
  isReadOnly: boolean;
  /**
   * Identifies the interface element that initiated the drag action.
   * Used for tracking purposes.
   */
  dragSource: PartyDragSource;
}

interface DragConnectProps {
  isDragging: boolean;
  connectDragPreview: ConnectDragPreview;
  connectDragSource: ConnectDragSource;
}

export interface Props extends IIconButton {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  IconComponent?(props: any): JSX.Element;
  onSuccessfulDrop?(): void;
  dragMode: ServicePartyDragMode;
}

class PartyMoveButton extends React.PureComponent<Props & DragConnectProps> {
  componentDidMount(): void {
    if (this.props.connectDragPreview) {
      // Use empty image as a drag preview so browsers don't draw it
      // and we can draw whatever we want on the custom drag layer instead.
      this.props.connectDragPreview(getEmptyImage(), {
        // IE fallback: specify that we'd rather screenshot the node
        // when it already knows it's being dragged so we can hide it with CSS.
        captureDraggingState: true,
      });
    }
  }

  render() {
    const { connectDragSource, isReadOnly, onClick } = this.props;

    return (
      <Button
        disabled={isReadOnly}
        startIcon={<IconGrab fontSize="large" />}
        variant="contained"
        color="primary"
        size="large"
        css={css`
          flex: 1 1 auto;
        `}
        onClick={onClick}
        innerRef={(ref) => connectDragSource(ref)}
      >
        Move
      </Button>
    );
  }
}

export default DragSource<Props, DragConnectProps>(
  DndItemTypes.PARTY,
  {
    beginDrag(props: Props) {
      return partySource.beginDrag({
        dragSource: props.dragSource,
        party: props.party,
        onSuccessfulDrop: props.onSuccessfulDrop,
      });
    },
    canDrag(props: Props) {
      return partySource.canDrag({
        party: props.party,
        isReadOnly: props.isReadOnly,
        dragMode: props.dragMode,
      });
    },
  },
  collectSource
)(PartyMoveButton);
